import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  createScratchCampaign,
  listScratchCampaigns,
  setScratchCampaignHidden,
  updateScratchCampaign,
  type ScratchCampaignMediaFiles,
} from '@/lib/api/scratch-campaigns.service'
import { ApiClientError } from '@/lib/api/client'
import type {
  RaspadinhaCampaign,
  RaspadinhaCampaignCreateInput,
  RaspadinhaCampaignUpdateInput,
} from '@/types/raspadinha-campaign'

type RaspadinhaCampaignsContextValue = {
  campaigns: RaspadinhaCampaign[]
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
  getById: (id: number) => RaspadinhaCampaign | null
  createCampaign: (input: RaspadinhaCampaignCreateInput) => Promise<RaspadinhaCampaign>
  updateCampaign: (
    id: number,
    input: RaspadinhaCampaignUpdateInput,
    media?: ScratchCampaignMediaFiles
  ) => Promise<RaspadinhaCampaign>
  setHiddenFromMain: (id: number, hidden: boolean) => Promise<RaspadinhaCampaign>
}

const RaspadinhaCampaignsContext = createContext<RaspadinhaCampaignsContextValue | undefined>(
  undefined
)

export function RaspadinhaCampaignsProvider({ children }: { children: ReactNode }) {
  const [campaigns, setCampaigns] = useState<RaspadinhaCampaign[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await listScratchCampaigns()
      setCampaigns(data)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível carregar as campanhas raspadinha.'
      setError(message)
      setCampaigns([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const getById = useCallback(
    (id: number) => campaigns.find((campaign) => campaign.id === id) ?? null,
    [campaigns]
  )

  const createCampaign = useCallback(async (input: RaspadinhaCampaignCreateInput) => {
    const created = await createScratchCampaign(input)
    await refresh()
    return created
  }, [refresh])

  const updateCampaign = useCallback(
    async (
      id: number,
      input: RaspadinhaCampaignUpdateInput,
      media: ScratchCampaignMediaFiles = {}
    ) => {
      const updated = await updateScratchCampaign(id, input, media)
      setCampaigns((current) =>
        current.map((campaign) => (campaign.id === id ? updated : campaign))
      )
      return updated
    },
    []
  )

  const setHiddenFromMainHandler = useCallback(
    async (id: number, hidden: boolean) => {
      const updated = await setScratchCampaignHidden(id, hidden)
      setCampaigns((current) =>
        current.map((campaign) => (campaign.id === id ? updated : campaign))
      )
      return updated
    },
    []
  )

  const value = useMemo(
    () => ({
      campaigns,
      isLoading,
      error,
      refresh,
      getById,
      createCampaign,
      updateCampaign,
      setHiddenFromMain: setHiddenFromMainHandler,
    }),
    [
      campaigns,
      createCampaign,
      error,
      getById,
      isLoading,
      refresh,
      setHiddenFromMainHandler,
      updateCampaign,
    ]
  )

  return (
    <RaspadinhaCampaignsContext.Provider value={value}>
      {children}
    </RaspadinhaCampaignsContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRaspadinhaCampaigns(): RaspadinhaCampaignsContextValue {
  const context = useContext(RaspadinhaCampaignsContext)
  if (!context) {
    throw new Error('useRaspadinhaCampaigns deve ser usado dentro de RaspadinhaCampaignsProvider')
  }
  return context
}
